import { useEffect, useState } from "react";
import Title from "../../components/common/title/Title";
import Pagination from "../../components/common/pagination/Pagination";
import Select from "../../components/common/inputs/Select";
import { getApi, putApi, deleteApi, postApi } from "../../services/api/globalApi";
import {
  successMessage,
  errorMessage,
} from "../../utils/toastify-message/ToastifyMessage";

export default function ReviewsList() {
  const [reviews, setReviews] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [hasRepliesFilter, setHasRepliesFilter] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const getReviews = async (params = {}) => {
    setLoading(true);
    const path = "admin/reviews";
    try {
      const response = await getApi({
        path,
        params: { page: currentPage, limit: 20, ...params },
      });
      const reviewsData = response?.data?.result?.data || [];
      setReviews(reviewsData);
      setPagination(response?.data?.result);
    } catch (error) {
      console.log(error);
      errorMessage("خطا در دریافت نظرات");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = {};
    if (statusFilter) {
      params.status = statusFilter;
    }
    if (hasRepliesFilter) {
      params.hasReplies = hasRepliesFilter;
    }
    if (sortBy) {
      params.sortBy = sortBy;
    }
    getReviews(params);
  }, [currentPage, statusFilter, hasRepliesFilter, sortBy]);

  const handleStatusChange = async (reviewId, newStatus) => {
    const path = `admin/reviews/${reviewId}/status`;
    try {
      await putApi({ path, body: { status: newStatus } });
      successMessage("وضعیت نظر با موفقیت تغییر کرد");
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (hasRepliesFilter) params.hasReplies = hasRepliesFilter;
      if (sortBy) params.sortBy = sortBy;
      getReviews(params);
    } catch (error) {
      console.log(error);
      errorMessage("خطا در تغییر وضعیت نظر");
    }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm("آیا از حذف این نظر اطمینان دارید؟")) {
      return;
    }

    const path = `admin/reviews/${reviewId}`;
    try {
      await deleteApi({ path });
      successMessage("نظر با موفقیت حذف شد");
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (hasRepliesFilter) params.hasReplies = hasRepliesFilter;
      if (sortBy) params.sortBy = sortBy;
      getReviews(params);
    } catch (error) {
      console.log(error);
      errorMessage("خطا در حذف نظر");
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { text: "در انتظار", class: "bg-yellow-100 text-yellow-800" },
      APPROVED: { text: "تایید شده", class: "bg-green-100 text-green-800" },
      REJECTED: { text: "رد شده", class: "bg-red-100 text-red-800" },
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-bold ${config.class}`}
      >
        {config.text}
      </span>
    );
  };

  const getRatingStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <svg
            key={star}
            className={`w-4 h-4 ${
              star <= rating ? "text-yellow-400" : "text-gray-300"
            }`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
    );
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-3xl p-6">
      <Title title="مدیریت نظرات" />

      <div className="w-full flex flex-col flex-wrap my-8 gap-6 px-6">
        <div className="w-full flex flex-col sm:flex-row gap-6">
          <div className="w-full sm:w-4/12">
            <Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              useFormik={false}
              className="bg-white text-center sm:text-right"
            >
              <option value="">همه نظرات</option>
              <option value="PENDING">در انتظار تایید</option>
              <option value="APPROVED">تایید شده</option>
              <option value="REJECTED">رد شده</option>
            </Select>
          </div>

          <div className="w-full sm:w-4/12">
            <Select
              value={hasRepliesFilter}
              onChange={(e) => {
                setHasRepliesFilter(e.target.value);
                setCurrentPage(1);
              }}
              useFormik={false}
              className="bg-white text-center sm:text-right"
            >
              <option value="">همه نظرات</option>
              <option value="true">پاسخ داده شده</option>
              <option value="false">پاسخ داده نشده</option>
            </Select>
          </div>

          <div className="w-full sm:w-4/12">
            <Select
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value);
                setCurrentPage(1);
              }}
              useFormik={false}
              className="bg-white text-center sm:text-right"
            >
              <option value="newest">جدیدترین</option>
              <option value="mostLiked">پربازدیدترین</option>
            </Select>
          </div>
        </div>
      </div>

      <div>
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p className="text-lg font-bold">نظری یافت نشد</p>
          </div>
        ) : (
          <div className="space-y-6">
            {reviews.map((review) => (
              <ReviewCard
                key={review._id}
                review={review}
                onStatusChange={handleStatusChange}
                onDelete={handleDelete}
                onReplyAdded={() => {
                  const params = {};
                  if (statusFilter) params.status = statusFilter;
                  if (hasRepliesFilter) params.hasReplies = hasRepliesFilter;
                  if (sortBy) params.sortBy = sortBy;
                  getReviews(params);
                }}
                getRatingStars={getRatingStars}
                getStatusBadge={getStatusBadge}
              />
            ))}
          </div>
        )}
      </div>

      {pagination && pagination.totalPages > 1 && (
        <Pagination
          page={currentPage}
          setPage={setCurrentPage}
          totalPages={pagination.totalPages}
        />
      )}
    </div>
  );
}
function ReplyCard({ reply, getStatusBadge, onReplyUpdated }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(reply.content);
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
  const maxLength = 500;

  const handleReplyStatusChange = async (replyId, newStatus) => {
    const path = `admin/reviews/replies/${replyId}/status`;
    try {
      await putApi({ path, body: { status: newStatus } });
      successMessage("وضعیت پاسخ با موفقیت تغییر کرد");
      if (onReplyUpdated) onReplyUpdated();
    } catch (error) {
      console.log(error);
      errorMessage("خطا در تغییر وضعیت پاسخ");
    }
  };

  const handleReplyDelete = async (replyId) => {
    if (!window.confirm("آیا از حذف این پاسخ اطمینان دارید؟")) {
      return;
    }

    const path = `admin/reviews/replies/${replyId}`;
    try {
      await deleteApi({ path });
      successMessage("پاسخ با موفقیت حذف شد");
      if (onReplyUpdated) onReplyUpdated();
    } catch (error) {
      console.log(error);
      errorMessage("خطا در حذف پاسخ");
    }
  };

  const handleEditReply = async () => {
    if (!editContent.trim()) {
      errorMessage("متن پاسخ نمی‌تواند خالی باشد");
      return;
    }

    setIsSubmittingEdit(true);
    try {
      const path = `admin/reviews/replies/${reply._id}`;
      await putApi({ path, body: { content: editContent } });
      successMessage("پاسخ با موفقیت ویرایش شد");
      setIsEditing(false);
      if (onReplyUpdated) onReplyUpdated();
    } catch (error) {
      console.log(error);
      errorMessage("خطا در ویرایش پاسخ");
    } finally {
      setIsSubmittingEdit(false);
    }
  };

  const handleCancelEdit = () => {
    setEditContent(reply.content);
    setIsEditing(false);
  };

  return (
    <div className={`rounded-2xl p-4 sm:p-5 border mr-0 sm:mr-10 md:mr-14 mt-4 shadow-sm ${reply.isManager ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'}`}>
      <div className="flex items-start gap-3 sm:gap-4">
        <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center flex-shrink-0 ${reply.isManager ? 'bg-green-600' : 'bg-green-50'}`}>
          {reply.isManager ? (
            <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
            </svg>
          ) : (
            <span className="text-xl sm:text-2xl font-bold text-gray-700">{reply.user?.name?.charAt(0) || '؟'}</span>
          )}
        </div>

        <div className="flex-1 flex flex-col gap-2 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="font-bold text-base sm:text-lg text-gray-900">
              {reply.isManager ? 'مدیر' : (reply.user?.name || "کاربر")}
            </h4>
            {reply.isManager && (
              <span className="bg-green-600 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                مدیر
              </span>
            )}
            {!reply.isManager && getStatusBadge(reply.status)}
          </div>
          <span className="text-xs text-gray-500">
            {new Date(reply.createdAt).toLocaleDateString("fa-IR")}
          </span>

      {isEditing ? (
        <div className="mt-3">
          <div className="relative mb-3">
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value.slice(0, maxLength))}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent min-h-[120px] resize-none"
              disabled={isSubmittingEdit}
              placeholder="متن پاسخ خود را ویرایش کنید..."
            />
            <span className="absolute bottom-3 left-3 text-xs text-gray-400">
              {editContent.length} / {maxLength}
            </span>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={handleEditReply}
              disabled={isSubmittingEdit || !editContent.trim()}
              className="w-full sm:w-auto px-5 py-2.5 text-sm font-bold text-white bg-green-600 hover:bg-green-700 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmittingEdit ? "در حال ذخیره..." : "ذخیره"}
            </button>
            <button
              onClick={handleCancelEdit}
              disabled={isSubmittingEdit}
              className="w-full sm:w-auto px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-xl transition-colors"
            >
              انصراف
            </button>
          </div>
        </div>
      ) : (
        <>
          <p className="text-sm sm:text-base text-gray-700 leading-7 mb-4 mt-1">{reply.content}</p>

          <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-gray-200">
            {reply.isManager ? (
              <>
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                >
                  ویرایش
                </button>
                <button
                  onClick={() => handleReplyDelete(reply._id)}
                  className="px-4 py-2 text-sm font-medium text-white bg-gray-700 hover:bg-gray-800 rounded-lg transition-colors"
                >
                  حذف
                </button>
              </>
            ) : (
              <>
                {reply.status !== "APPROVED" && (
              <button
                onClick={() => handleReplyStatusChange(reply._id, "APPROVED")}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
              >
                تایید
              </button>
            )}
            {reply.status !== "REJECTED" && (
              <button
                onClick={() => handleReplyStatusChange(reply._id, "REJECTED")}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                رد
              </button>
            )}
            {reply.status !== "PENDING" && (
              <button
                onClick={() => handleReplyStatusChange(reply._id, "PENDING")}
                className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded-lg transition-colors"
              >
                در انتظار
              </button>
            )}
                <button
                  onClick={() => handleReplyDelete(reply._id)}
                  className="px-4 py-2 text-sm font-medium text-white bg-gray-700 hover:bg-gray-800 rounded-lg transition-colors"
                >
                  حذف
                </button>
              </>
            )}
          </div>
        </>
      )}
        </div>
      </div>
    </div>
  );
}

function ReviewCard({ review, onStatusChange, onDelete, onReplyAdded, getRatingStars, getStatusBadge }) {
  const [showReplies, setShowReplies] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyContent, setReplyContent] = useState("");
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const [replies, setReplies] = useState([]);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [replyCount, setReplyCount] = useState(0);

  const MAX_CONTENT_LENGTH = 300;
  const shouldShowReadMore = review.content.length > MAX_CONTENT_LENGTH;

  useEffect(() => {
    const fetchReplyCount = async () => {
      try {
        const response = await getApi({
          path: "admin/reviews/replies",
          params: { reviewId: review._id, limit: 1 },
        });
        setReplyCount(response?.data?.result?.totalDocs || 0);
      } catch (error) {
        console.log(error);
      }
    };
    fetchReplyCount();
  }, [review._id]);

  const fetchReplies = async (forceRefresh = false) => {
    if (!forceRefresh && replies.length > 0) return;

    setLoadingReplies(true);
    try {
      const response = await getApi({
        path: "admin/reviews/replies",
        params: { reviewId: review._id, limit: 100 },
      });
      const repliesData = response?.data?.result?.data || [];
      setReplies(repliesData);
      setReplyCount(response?.data?.result?.totalDocs || repliesData.length);
    } catch (error) {
      console.log(error);
      errorMessage("خطا در دریافت پاسخ‌ها");
    } finally {
      setLoadingReplies(false);
    }
  };

  const handleToggleReplies = async () => {
    if (!showReplies) {
      await fetchReplies();
    }
    setShowReplies(!showReplies);
  };

  const handleSubmitReply = async () => {
    if (!replyContent.trim()) {
      errorMessage("لطفا متن پاسخ را وارد کنید");
      return;
    }

    setIsSubmittingReply(true);
    try {
      await postApi({
        path: "api/reviews/reply",
        body: {
          reviewId: review._id,
          content: replyContent,
        },
      });
      successMessage("پاسخ با موفقیت ثبت شد");
      setReplyContent("");
      setShowReplyForm(false);
      await fetchReplies(true); 
      if (onReplyAdded) onReplyAdded();
    } catch (error) {
      console.log(error);
      const errorMsg =
        error?.response?.data?.errors?.[0]?.message ||
        error?.response?.data?.messages?.[0]?.message ||
        "خطا در ثبت پاسخ";
      errorMessage(errorMsg);
    } finally {
      setIsSubmittingReply(false);
    }
  };

  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-4 sm:p-6 hover:shadow-lg transition-shadow">
      {/* Header */}
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0">
          <span className="text-lg sm:text-xl font-bold text-gray-700">
            {review.user?.name?.charAt(0) || "ک"}
          </span>
        </div>
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-bold text-gray-900 text-sm sm:text-base">
              {review.user?.name || "نامشخص"}
            </h4>
            {review.isAnonymous && (
              <span className="bg-gray-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                ناشناس
              </span>
            )}
            {review.isPurchaser && (
              <span className="bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                خریدار
              </span>
            )}
            {getStatusBadge(review.status)}
          </div>
          <span className="text-xs sm:text-sm text-gray-500">
            {new Date(review.createdAt).toLocaleDateString("fa-IR")}
          </span>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className="font-bold text-gray-900 text-sm">{review.rating}.0</span>
            {getRatingStars(review.rating)}
          </div>
          {review.user?.mobile && (
            <span className="text-xs text-gray-500 mt-1">
              {review.user.mobile}
            </span>
          )}
        </div>
      </div>

      {review.product && (
        <div className="bg-gray-50 rounded-lg p-3 mb-4">
          <div className="text-sm text-gray-700">
            <span className="font-bold">محصول:</span> {review.product?.name || "نامشخص"}
            {review.product?.sku && (
              <span className="text-xs text-gray-500 mr-2">کد: {review.product.sku}</span>
            )}
          </div>
        </div>
      )}

      <div className="mb-4">
        <p className="text-gray-800 leading-7 text-justify">
          {isExpanded || !shouldShowReadMore
            ? review.content
            : `${review.content.substring(0, MAX_CONTENT_LENGTH)}...`}
        </p>
        {shouldShowReadMore && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-green-600 hover:text-green-800 font-medium text-sm mt-2 inline-flex items-center gap-1"
          >
            {isExpanded ? (
              <>
                <span>مشاهده کمتر</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
              </>
            ) : (
              <>
                <span>مشاهده بیشتر</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </>
            )}
          </button>
        )}
      </div>

      {(review.pendingContent || review.pendingRating) && (
        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h5 className="font-bold text-yellow-800 text-sm">تغییرات در انتظار تایید:</h5>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={async () => {
                  try {
                    await putApi({ path: `admin/reviews/${review._id}/approve-pending` });
                    successMessage("تغییرات با موفقیت تایید شد");
                    window.location.reload();
                  } catch (error) {
                    console.log(error);
                    errorMessage("خطا در تایید تغییرات");
                  }
                }}
                className="px-3 py-1.5 text-xs font-bold text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors flex items-center gap-1"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                تایید
              </button>
              <button
                onClick={async () => {
                  try {
                    await putApi({ path: `admin/reviews/${review._id}/reject-pending` });
                    successMessage("تغییرات رد شد");
                    window.location.reload();
                  } catch (error) {
                    console.log(error);
                    errorMessage("خطا در رد تغییرات");
                  }
                }}
                className="px-3 py-1.5 text-xs font-bold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors flex items-center gap-1"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                رد
              </button>
            </div>
          </div>
          {review.pendingRating && (
            <div className="mb-2 flex items-center gap-2">
              <span className="text-xs font-medium text-yellow-700">امتیاز جدید:</span>
              <div className="flex items-center gap-1.5">
                <span className="font-bold text-yellow-800 text-sm">{review.pendingRating}.0</span>
                {getRatingStars(review.pendingRating)}
              </div>
            </div>
          )}
          {review.pendingContent && (
            <div>
              <span className="text-xs font-medium text-yellow-700 block mb-1">محتوای جدید:</span>
              <p className="text-sm text-yellow-800 leading-6 bg-white p-3 rounded-lg border border-yellow-200">
                {review.pendingContent}
              </p>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-4 text-sm text-gray-600 mb-4 pb-4 border-b border-gray-200">
        <span className="flex items-center gap-1.5">
          <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
          </svg>
          <span className="font-bold">{review.likes || 0}</span>
        </span>
        <span className="flex items-center gap-1.5">
          <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
            <path d="M18 9.5a1.5 1.5 0 11-3 0v-6a1.5 1.5 0 013 0v6zM14 9.667v-5.43a2 2 0 00-1.105-1.79l-.05-.025A4 4 0 0011.055 2H5.64a2 2 0 00-1.962 1.608l-1.2 6A2 2 0 004.44 12H8v4a2 2 0 002 2 1 1 0 001-1v-.667a4 4 0 01.8-2.4l1.4-1.866a4 4 0 00.8-2.4z" />
          </svg>
          <span className="font-bold">{review.dislikes || 0}</span>
        </span>
      </div>

      <div className="flex flex-col gap-4 mb-4">
        <div className="flex items-center gap-3 sm:gap-4">
          {replyCount > 0 && (
            <button
              onClick={handleToggleReplies}
              className="flex items-center gap-1.5 text-gray-500 hover:text-green-600 transition-colors font-medium text-xs sm:text-sm"
            >
              <span>{replyCount} پاسخ‌ها</span>
              <svg className={`w-4 h-4 transition-transform ${showReplies ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          )}

          <button
            onClick={() => setShowReplyForm(!showReplyForm)}
            className="flex items-center gap-1.5 text-gray-500 hover:text-green-600 transition-colors font-medium text-xs sm:text-sm"
          >
            <svg className="w-4 h-4 -scale-x-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
            </svg>
            پاسخ
          </button>
        </div>

        <div className="bg-gray-50 rounded-lg p-3">
          <div className="flex flex-wrap items-center gap-2">
            {review.status !== "APPROVED" && (
              <button
                onClick={() => onStatusChange(review._id, "APPROVED")}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
              >
                تایید
              </button>
            )}

            {review.status !== "REJECTED" && (
              <button
                onClick={() => onStatusChange(review._id, "REJECTED")}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                رد
              </button>
            )}

            {review.status !== "PENDING" && (
              <button
                onClick={() => onStatusChange(review._id, "PENDING")}
                className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded-lg transition-colors"
              >
                در انتظار
              </button>
            )}

            <button
              onClick={() => onDelete(review._id)}
              className="px-4 py-2 text-sm font-medium text-white bg-gray-700 hover:bg-gray-800 rounded-lg transition-colors"
            >
              حذف
            </button>
          </div>
        </div>
      </div>

      {showReplyForm && (
        <div className="bg-white rounded-2xl p-4 sm:p-6 mb-4 border-2 border-dashed border-green-200">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-full bg-green-600 flex items-center justify-center text-white font-bold flex-shrink-0">
              A
            </div>
            <div>
              <span className="font-bold text-gray-900 block text-sm">پاسخ ادمین</span>
              <span className="text-xs text-gray-500">پاسخ شما بلافاصله منتشر می‌شود</span>
            </div>
          </div>

          <div className="relative mb-2">
            <textarea
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value.slice(0, 500))}
              placeholder="پاسخ خود را بنویسید..."
              className="w-full border border-gray-200 rounded-xl px-3 py-3 sm:px-4 sm:py-4 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent min-h-[120px] resize-none"
              disabled={isSubmittingReply}
            />
            <span className="absolute bottom-3 left-3 text-xs text-gray-400">
              {replyContent.length} / 500
            </span>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleSubmitReply}
              disabled={isSubmittingReply || !replyContent.trim()}
              className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-8 rounded-xl transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed order-1"
            >
              {isSubmittingReply ? "در حال ارسال..." : "ارسال پاسخ"}
            </button>
            <button
              onClick={() => {
                setShowReplyForm(false);
                setReplyContent("");
              }}
              className="w-full sm:w-auto bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-bold py-3 px-8 rounded-xl transition-colors text-sm order-2"
              disabled={isSubmittingReply}
            >
              انصراف
            </button>
          </div>
        </div>
      )}

      {showReplies && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          {loadingReplies ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
            </div>
          ) : replies.length === 0 ? (
            <p className="text-center text-gray-500 py-4">هنوز پاسخی ثبت نشده است</p>
          ) : (
            <div className="space-y-4">
              {replies.map((reply) => (
                <ReplyCard
                  key={reply._id}
                  reply={reply}
                  getStatusBadge={getStatusBadge}
                  onReplyUpdated={() => fetchReplies(true)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
